import { AxiosResponse } from 'axios'
import _ from 'lodash'
import { all, call, put, select, takeLatest } from 'redux-saga/effects'
import { getType } from 'typesafe-actions'

import { ReportType } from '../../containers/CompanyDetail/Reports'
import { apiCall } from '../../utils/api'
import { waitForConfigLoad } from '../../utils/sagaUtils'
import { withLoadingParametrized } from '../loading'
import { getCountryCode } from '../config/selectors'
import { isUserLoggedIn } from '../user/selectors'

import { CompanyDetailActions } from './actions'
import { getCompanyByIdentifier } from './selectors'
import { CompanyDetail, Company, KeyValue } from './types'

const pageSize = 10

export type CompanyDetailResponse = {
  data: {
    id: string
    matchLevel: string
    name?: string
    hqAddress: {
      countryCode?: string
      regionCode?: string
      city?: string
      locationType?: string
      zipCode?: string
      fullAddress?: string
      street?: string
      houseNumber?: string
    }
    legalRepresentative?: string
    contactPhone?: string
    npwpCode?: string
    legalizationCode?: string
    registrationCodes?: string[]
    reports: {
      bifName: string
      orderingName: string | undefined
      code: string
      languages: string[]
      type: string
      lastUpdateDate?: string
      craConsentRequired: boolean
      materialTypePriceParam?: string
      countryCodePriceParam?: string
      isFinancialPriceParam?: string
      inRevisionPriceParam?: string
      dataTypePriceParam?: string
      providerName?: string
      providerCode?: string
      deliveryOptions: {
        code: string
        currency?: string
        displayNetValue?: string
        maxInDays: number
        minInDays: number
        netValue?: number
      }[]
    }[]
    incorporationDate?: string
    lastUpdateDate?: string
    proofOfInterest?: string
  }
  areDocumentsInPriceList: boolean
  documentsLimitReached: boolean
  documentsKeyValues?: KeyValue[]
}

export const mapCompanyDetailResponse = (res: CompanyDetailResponse, companyIdentifier: string): Company => ({
  ...res.data,
  companyIdentifier,
  reports: _.map(res.data.reports, x => ({
    ...x,
    orderingName: x.orderingName,
    craConsentRequired: x.craConsentRequired,
    type: x.type as ReportType
  }))
})

export function* loadAndSetCompanyDetail(
  companyIdentifier: string,
  officialDocumentsRequested: boolean,
  selectedDocumentCode: string | undefined,
  documentsToFetch: number,
  formerName?: string
) {
  yield call(waitForConfigLoad)

  const countryCode: string = yield select(getCountryCode)
  const isLoggedIn: boolean = yield select(isUserLoggedIn)

  const { data }: AxiosResponse<CompanyDetailResponse> = yield call(apiCall, {
    // ensure that relative path is used to allow SSR which accesses app from different URL than public
    url: `/api${isLoggedIn ? '' : '/public'}/Search/detail/${countryCode}/${companyIdentifier}`,
    method: 'get',
    params: {
      productCategory: officialDocumentsRequested ? 'All' : undefined,
      selectedDocumentCode,
      documentsToFetch,
      formerName
    }
  })

  yield put(
    CompanyDetailActions.setLoadedCompany({
      companyId: data.data.id,
      company: mapCompanyDetailResponse(data, companyIdentifier),
      officialDocumentRequested: officialDocumentsRequested,
      selectedDocumentCode: selectedDocumentCode,
      fetchedDocuments: documentsToFetch,
      areDocumentsInPriceList: data.areDocumentsInPriceList,
      documentsKeyValues: data.documentsKeyValues ?? [],
      documentsLimitReached: data.documentsLimitReached
    })
  )
}

export function* loadCompanyDetailByState(action: ReturnType<typeof CompanyDetailActions.loadCompanyByState>) {
  const { companyIdentifier, formerName } = action.payload

  const companyDetail: CompanyDetail = yield select(getCompanyByIdentifier, { companyIdentifier })

  yield loadAndSetCompanyDetail(
    companyIdentifier,
    companyDetail && companyDetail.officialDocumentsRequested,
    companyDetail?.selectedDocumentCode,
    companyDetail?.fetchedDocuments ?? pageSize,
    formerName
  )
}

export function* loadCompanyDetail(action: ReturnType<typeof CompanyDetailActions.loadCompany>) {
  const { companyIdentifier, includeOfficialDocuments, formerName } = action.payload

  const companyDetail: CompanyDetail = yield select(getCompanyByIdentifier, { companyIdentifier })

  yield loadAndSetCompanyDetail(
    companyIdentifier,
    includeOfficialDocuments ?? false,
    companyDetail?.selectedDocumentCode,
    companyDetail?.fetchedDocuments ?? pageSize,
    formerName
  )
}

export function* loadCompanyDetailWithMoreDocuments(
  action: ReturnType<typeof CompanyDetailActions.loadCompanyWithMoreDocuments>
) {
  const { companyIdentifier, formerName } = action.payload

  const companyDetail: CompanyDetail = yield select(getCompanyByIdentifier, { companyIdentifier })

  const requestWithOfficialDocuments = true
  yield loadAndSetCompanyDetail(
    companyIdentifier,
    requestWithOfficialDocuments,
    companyDetail?.selectedDocumentCode,
    (companyDetail?.fetchedDocuments ?? pageSize) + pageSize,
    formerName
  )
}

export function* loadCompanyDetailWithSelectedDocument(
  action: ReturnType<typeof CompanyDetailActions.loadCompanyWithSelectedDocument>
) {
  const { companyIdentifier, documentCode, formerName } = action.payload

  const companyDetail: CompanyDetail = yield select(getCompanyByIdentifier, { companyIdentifier })

  const requestWithOfficialDocuments = true
  yield loadAndSetCompanyDetail(
    companyIdentifier,
    requestWithOfficialDocuments,
    documentCode,
    companyDetail?.fetchedDocuments ?? pageSize,
    formerName
  )
}

/* istanbul ignore next */
export function* companyDetailSagas() {
  yield all([
    takeLatest(
      getType(CompanyDetailActions.loadCompanyByState),
      withLoadingParametrized('companyDetail', loadCompanyDetailByState, x => x.payload.companyIdentifier)
    ),
    takeLatest(
      getType(CompanyDetailActions.loadCompany),
      withLoadingParametrized('companyDetail', loadCompanyDetail, x => x.payload.companyIdentifier)
    ),
    takeLatest(
      getType(CompanyDetailActions.loadCompanyWithMoreDocuments),
      withLoadingParametrized('companyDetail', loadCompanyDetailWithMoreDocuments, x => x.payload.companyIdentifier)
    ),
    takeLatest(
      getType(CompanyDetailActions.loadCompanyWithSelectedDocument),
      withLoadingParametrized('companyDetail', loadCompanyDetailWithSelectedDocument, x => x.payload.companyIdentifier)
    )
  ])
}
